/**
 * HelpChat — Natural language conversation-based help assistant
 *
 * Provides an AI-powered chat interface within the Help page for answering
 * user questions about Nabhos features and workflows.
 */

import { useState, useEffect, useRef } from 'react';
import { Send, X, Minimize2, Maximize2, Lightbulb } from 'lucide-react';
import { helpChatApi } from '../../services/api';
import Card from './Card';
import { cn } from '../../lib/utils';
import { ARTICLES } from '../../pages/HelpPage';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

interface HelpChatProps {
  onClose?: () => void;
  isMinimized?: boolean;
  onMinimizeChange?: (minimized: boolean) => void;
}

const HelpChat = ({ onClose, isMinimized = false, onMinimizeChange }: HelpChatProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestedTopics, setSuggestedTopics] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ── Article retrieval ──────────────────────────────────────────────────────
  /**
   * Serialise a single article into a compact plain-text block that can be
   * injected into the Groq prompt without wasting tokens.
   */
  const articleToText = (article: (typeof ARTICLES)[0]): string => {
    const lines: string[] = [`[${article.section}] ${article.title}`];
    for (const block of article.blocks) {
      if (block.text) lines.push(block.text);
      if (block.items) lines.push(...block.items.map((i) => `• ${i}`));
      if (block.headers && block.rows) {
        lines.push(block.headers.join(' | '));
        block.rows.forEach((row) => lines.push(row.join(' | ')));
      }
    }
    return lines.join('\n');
  };

  /**
   * Return up to `limit` articles whose keywords or text match any word in
   * the query. Falls back to an empty string (no context) when nothing matches.
   */
  const getRelevantContext = (query: string, limit = 3): string => {
    const q = query.trim().toLowerCase();
    if (!q) return '';
    const words = q.split(/\s+/);
    const scored = ARTICLES.map((article) => {
      const corpus = [
        article.section,
        article.title,
        ...article.keywords,
        ...article.blocks.flatMap((b) => [
          b.text ?? '',
          ...(b.items ?? []),
          ...(b.rows ?? []).flat(),
        ]),
      ]
        .join(' ')
        .toLowerCase();
      const score = words.filter((w) => corpus.includes(w)).length;
      return { article, score };
    });
    const matches = scored
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map(({ article }) => articleToText(article));
    return matches.join('\n\n---\n\n');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Add welcome message on first load
  useEffect(() => {
    if (messages.length === 0) {
      const welcomeMessage: Message = {
        id: '0',
        role: 'assistant',
        content:
          "Hi! 👋 I'm your Nabhos assistant. I can help you understand features, troubleshoot issues, and answer questions about the platform. What can I help you with today?",
        timestamp: new Date().toISOString(),
      };
      setMessages([welcomeMessage]);
    }
  }, []);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessageContent = inputValue;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: userMessageContent,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);
    setSuggestedTopics([]);

    try {
      // Filter out welcome message (id='0') for API - Gemini requires first message to be from user
      const historyForApi = messages
        .filter((m) => m.id !== '0')
        .map((m) => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
        }));

      // Find relevant help articles for this query and send them with the request
      const relevantContext = getRelevantContext(userMessageContent);

      // Call help chat API
      const response = await helpChatApi.chat({
        message: userMessageContent,
        conversationHistory: historyForApi,
        relevantContext: relevantContext || undefined,
      });

      if (response.success) {
        // Check if service returned an error (e.g., quota exceeded)
        if (response.data?.error) {
          setError(response.data.error);
          return;
        }

        // Success case with response
        if (response.data?.response) {
          const assistantMessage: Message = {
            id: Date.now().toString(),
            role: 'assistant',
            content: response.data.response,
            timestamp: response.data.timestamp,
          };

          setMessages((prev) => [...prev, assistantMessage]);

          // Get suggested topics for follow-up
          try {
            const suggestionsResponse = await helpChatApi.getSuggestions(userMessage.content);
            if (
              suggestionsResponse.success &&
              Array.isArray(suggestionsResponse.data?.topics)
            ) {
              setSuggestedTopics(suggestionsResponse.data.topics);
            }
          } catch (err) {
            // Silently fail on suggestions
          }
        } else {
          setError('Failed to get response. Please try again.');
        }
      } else {
        setError('Failed to get response. Please try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedTopic = (topic: string) => {
    setInputValue(topic);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (isMinimized) {
    return (
      <Card className="w-full max-w-sm">
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary-500 to-primary-600">
          <div className="text-white font-semibold text-sm">Help Assistant</div>
          <div className="flex gap-2">
            <button
              onClick={() => onMinimizeChange?.(false)}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              title="Expand"
            >
              <Maximize2 className="w-4 h-4 text-white" />
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                title="Close"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            )}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-2xl flex flex-col h-[600px] shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">Help Assistant</h3>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Ask me anything about Nabhos
          </p>
        </div>
        <div className="flex gap-2">
          {onMinimizeChange && (
            <button
              onClick={() => onMinimizeChange(true)}
              className="p-2 hover:bg-white/50 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Minimize"
            >
              <Minimize2 className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/50 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>
          )}
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={cn(
              'flex',
              message.role === 'user' ? 'justify-end' : 'justify-start'
            )}
          >
            <div
              className={cn(
                'max-w-xs px-4 py-2.5 rounded-lg text-sm',
                message.role === 'user'
                  ? 'bg-primary-500 text-white rounded-br-none'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-none'
              )}
            >
              <p className="whitespace-pre-wrap">{message.content}</p>
              <p className="text-xs mt-1 opacity-70">
                {new Date(message.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2.5 rounded-lg">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: '0.2s' }}
                />
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: '0.4s' }}
                />
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="flex justify-center">
            <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 px-4 py-2.5 rounded-lg text-sm">
              {error}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggested topics */}
      {suggestedTopics.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-start gap-2 mb-2">
            <Lightbulb className="w-3.5 h-3.5 text-primary-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
              You might also ask about:
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {suggestedTopics.map((topic, idx) => (
              <button
                key={idx}
                onClick={() => handleSuggestedTopic(topic)}
                className="text-xs px-2.5 py-1 rounded-full bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-gray-600 transition-colors border border-primary-200 dark:border-primary-700"
              >
                {topic}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask your question..."
            disabled={isLoading}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            onClick={handleSendMessage}
            disabled={isLoading || !inputValue.trim()}
            className="px-3.5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            title="Send message"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Card>
  );
};

export default HelpChat;

// Made with Bob
